import React, { useState } from 'react';
import RewardCenterCardContainer from './RewardCenterCard';
import generateRewardCenterCardDataRendering from 'shared/screens/RewardCenter/utils/generateRewardCenterCardDataRendering';
import styled from 'styled-components';
import {
  formatOfferAmount,
  getOfferCardTitle,
  getOfferPriceValue,
  hasSoldOutState,
  isCoinOffer,
} from 'shared/screens/RewardCenter/utils/validations';
import { sendRewardCenterEvent } from 'shared/screens/RewardCenter/analytics/rewardCenterlogger';
import { REWARD_CENTER_EVENT_TYPES, REWARD_CENTER_OFFERS_STATUS } from 'shared/screens/RewardCenter/constants';
import { REWARD_CARD_LAYOUT } from 'shared/screens/RewardCenter/hooks/useRewardCardTypography';
import { isNotStandaloneMobilePWA, isStandalonePWA } from '../../../../utils/mobileSupported';

const L = REWARD_CARD_LAYOUT;
const useMobileWebOfferLayout = isStandalonePWA || isNotStandaloneMobilePWA;
const mobileWebOfferTitleBase = window.innerHeight <= 575 ? 0.9 : 0.95;

const RewardCenterOffers = ({ object, rewardCenterAssets, RewardsImageBaseURL, setSelectedOffer }) => {
  const { Frame, Banner, Shadow, Loyalty, Coins, Badge } = generateRewardCenterCardDataRendering(
    object,
    rewardCenterAssets
  );
  const { offerId, offerImage, ribbontype, isNew } = object;
  const isCoinPricing = isCoinOffer(object);
  const hasSoldOutStateValue = hasSoldOutState(ribbontype);
  const priceText = formatOfferAmount(getOfferPriceValue(object));
  const offerTitle = getOfferCardTitle(object);
  const offerPricingIcon = isCoinPricing ? Coins : Loyalty;

  const [offerImageLoaded, setOfferImageLoaded] = useState(false);

  const handleClick = () => {
    if (hasSoldOutStateValue) {
      return;
    }

    setSelectedOffer(object);
    sendRewardCenterEvent(REWARD_CENTER_EVENT_TYPES.OPEN_REWARD_CENTER_OFFER, { offer: object, offerId });
  };

  return (
    <RewardCenterCardContainer handleClick={handleClick} Frame={Frame}>
      {Banner && <CardRibbon src={Banner} />}
      {Shadow && <CardShadow src={Shadow} />}
      <CardContent ribbontype={ribbontype}>
        {offerImage && RewardsImageBaseURL && (
          <CardImage src={`${RewardsImageBaseURL}${offerImage}`} onLoad={() => setOfferImageLoaded(true)} />
        )}
        {offerImageLoaded && (
          <DescriptionWrapper>
            <TitleSlot>
              <OfferTitle hasSoldOutState={hasSoldOutStateValue}>{offerTitle}</OfferTitle>
            </TitleSlot>
            <PriceSlot>
              <OfferPriceRow>
                <OfferPricingIcon src={offerPricingIcon} $isCoinPricing={isCoinPricing} />
                <OfferPriceValue hasSoldOutState={hasSoldOutStateValue}>{priceText}</OfferPriceValue>
              </OfferPriceRow>
            </PriceSlot>
          </DescriptionWrapper>
        )}
        {isNew && <NewOfferBadge src={Badge} />}
      </CardContent>
    </RewardCenterCardContainer>
  );
};

export default RewardCenterOffers;

const CardImage = styled.img`
  width: 100%;
  object-fit: contain;
  position: relative;
  z-index: 1;
  border-top-left-radius: 10%;
  border-top-right-radius: 10%;
  height: 75%;
`;

const CardShadow = styled.img`
  position: absolute;
  height: 100%;
  width: 100%;
  object-fit: contain;
  z-index: 3;
  opacity: 0.5;
  left: 0rem;
`;

const CardRibbon = styled.img`
  width: 75%;
  bottom: 35rem;
  z-index: 5;
  position: absolute;
  top: -2.3rem;
  left: 2.8rem;
  @media only screen and (max-height: 650px) {
    top: -1.2rem;
    left: 1.4rem;
  }
  @media only screen and (min-height: 651px) and (max-height: 800px) {
    top: -1.5rem;
    left: 2rem;
  }
  @media only screen and (min-height: 801px) and (max-height: 900px) {
    top: -2rem;
    left: 2.5rem;
  }
  @media only screen and (min-height: 901px) and (max-height: 949px) {
    top: -2.3rem;
    left: 3rem;
  }
  @media only screen and (min-height: 950px) {
    top: -2.4rem;
  }

  ${({ theme }) =>
    theme.platform('android') &&
    theme.screens.device('mobile-standalone') &&
    `
    left: 1.8vw !important;
  `}
`;

const CardContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: ${({ ribbontype }) =>
    ribbontype === REWARD_CENTER_OFFERS_STATUS.SOLD_OUT ? '#818181' : '#ac7601'};
  border-radius: 10%;
`;

// Fixed slot heights + top-aligned content keep title and price baselines
// identical across every card in the row, mirroring mobile (REWARD_CARD_LAYOUT).
const DescriptionWrapper = styled.div`
  --title-fs: ${useMobileWebOfferLayout ? `${mobileWebOfferTitleBase}rem` : 'clamp(0.58rem, 0.92vw, 0.92rem)'};
  --price-fs: ${useMobileWebOfferLayout ? `${mobileWebOfferTitleBase}rem` : 'clamp(0.59rem, 0.92vw, 0.86rem)'};

  @media only screen and (min-height: 660px) {
    --title-fs: ${useMobileWebOfferLayout ? `${mobileWebOfferTitleBase}rem` : 'clamp(0.69rem, 1.07vw, 1.1rem)'};
    --price-fs: ${useMobileWebOfferLayout ? `${mobileWebOfferTitleBase}rem` : 'clamp(0.7rem, 0.97vw, 1.03rem)'};
  }
  @media only screen and (min-height: 900px) {
    --title-fs: ${useMobileWebOfferLayout ? `${mobileWebOfferTitleBase}rem` : 'clamp(0.81rem, 1.3vw, 1.15rem)'};
    --price-fs: ${useMobileWebOfferLayout ? `${mobileWebOfferTitleBase}rem` : 'clamp(0.81rem, 1.08vw, 1.13rem)'};
  }
  @media only screen and (min-height: 1000px) {
    --title-fs: ${useMobileWebOfferLayout ? `${mobileWebOfferTitleBase}rem` : 'clamp(0.88rem, 1.3vw, 1.26rem)'};
    --price-fs: ${useMobileWebOfferLayout ? `${mobileWebOfferTitleBase}rem` : 'clamp(0.81rem, 1.08vw, 1.13rem)'};
  }

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 90%;
  z-index: 5;
  position: relative;
  flex: 4;
  padding: 0 0.25rem;
  margin-bottom: 1rem;

  @media only screen and (max-height: 650px) {
    margin-bottom: 0;
  }
`;

const TitleSlot = styled.div`
  width: 100%;
  height: calc(var(--title-fs) * ${L.titleLineHeightFactor} * ${L.titleLines});
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  overflow: hidden;
`;

const PriceSlot = styled.div`
  width: 100%;
  height: calc(var(--price-fs) * ${L.priceLineHeightFactor} * ${L.priceLines});
  margin-top: calc(var(--title-fs) * ${L.titleLineHeightFactor} * 0.18);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`;

const OfferTitle = styled.p`
  font-size: var(--title-fs);
  line-height: ${L.titleLineHeightFactor};
  text-transform: uppercase;
  text-align: center;
  color: ${({ hasSoldOutState }) => (hasSoldOutState ? '#BCBBBA' : '#fff')};
  font-weight: 700;
  margin: 0;
  padding: 0 0.25rem;
  width: 100%;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: ${L.titleLines};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const OfferPriceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;

const OfferPricingIcon = styled.img`
  height: ${({ $isCoinPricing }) =>
    useMobileWebOfferLayout ? ($isCoinPricing ? '0.95rem' : '1.15rem') : $isCoinPricing ? '1.1rem' : '1.5rem'};
  width: ${({ $isCoinPricing }) =>
    useMobileWebOfferLayout ? ($isCoinPricing ? '0.95rem' : '1.15rem') : $isCoinPricing ? '1.1rem' : '1.5rem'};
  margin-right: 0.25rem;
  flex-shrink: 0;
  object-fit: contain;
`;

const OfferPriceValue = styled.p`
  font-size: var(--price-fs);
  line-height: ${L.priceLineHeightFactor};
  color: ${({ hasSoldOutState }) => (hasSoldOutState ? '#BCBBBA' : '#fff')};
  font-weight: 600;
  text-align: center;
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NewOfferBadge = styled.img`
  position: absolute;
  width: 80px;
  height: 80px;
  z-index: 10;
  left: 21rem;
  bottom: 36.2rem;
  filter: drop-shadow(-2px 4px 8px #000000);

  @media only screen and (min-height: 660px) and (max-height: 800px) {
    width: 65px;
    height: 65px;
    left: 12.5rem;
    bottom: 22.2rem;
  }
  @media only screen and (min-height: 801px) and (max-height: 900px) {
    left: 17rem;
    bottom: 29.2rem;
  }
`;
